import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormGroup,
  TextField,
  Alert,
  Box,
} from '@mui/material';
import { SKIP_REASONS } from './reviewHelpers';

/**
 * Modal for capturing structured skip reasons on a planned trade.
 *
 * Multi-select — tick all that apply (real skips are usually several things at
 * once). At least one reason is required; the free-text note is optional unless
 * "Other" is selected, in which case it's mandatory so we don't end up with an
 * audit trail of empty "other" rows.
 */
const SkipTradeDialog = ({ open, onClose, onConfirm, submitting, errorMessage }) => {
  const [reasons, setReasons] = useState([]);
  const [note, setNote] = useState('');
  const [validationError, setValidationError] = useState('');

  // Reset local state every time the dialog re-opens so a closed-then-reopened
  // dialog doesn't show stale input from the previous attempt.
  useEffect(() => {
    if (open) {
      setReasons([]);
      setNote('');
      setValidationError('');
    }
  }, [open]);

  const toggleReason = (value) => {
    setReasons((prev) =>
      prev.includes(value) ? prev.filter((r) => r !== value) : [...prev, value]
    );
    setValidationError('');
  };

  const handleConfirm = () => {
    if (reasons.length === 0) {
      setValidationError('Pick at least one reason so this skip is auditable.');
      return;
    }
    if (reasons.includes('other') && !note.trim()) {
      setValidationError('Describe the reason — "other" requires a note.');
      return;
    }
    setValidationError('');
    onConfirm({
      skipReasons: reasons,
      ...(note.trim() ? { skipNote: note.trim() } : {}),
    });
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Skip this trade</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Record why you're passing on this signal — tick all that apply. Skip
          reasons are reviewed so you can spot behavioural leaks (e.g.
          systematically skipping winners).
        </DialogContentText>

        <FormControl component="fieldset" fullWidth>
          <FormGroup>
            {SKIP_REASONS.map((option) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    checked={reasons.includes(option.value)}
                    onChange={() => toggleReason(option.value)}
                    disabled={submitting}
                  />
                }
                label={option.label}
              />
            ))}
          </FormGroup>
        </FormControl>

        <Box mt={2}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={reasons.includes('other') ? 'Reason (required)' : 'Additional note (optional)'}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={submitting}
            placeholder="Add context for your future-self review."
          />
        </Box>

        {(validationError || errorMessage) && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 0 }}>
            {validationError || errorMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="warning"
          variant="contained"
          disabled={submitting}
        >
          {submitting ? 'Skipping…' : 'Skip this trade'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SkipTradeDialog;
